import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, PrimaryButton, Spinner, SpinnerSize, Dropdown, IDropdownOption, Checkbox } from "@fluentui/react";
import { getAddGroupData, addGroup } from "../../../api/admin/api_admin";
import { AddGroupResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";

export const AddGroup = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<AddGroupResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [groupCd, setGroupCd] = useState<string>("");
    const [groupName, setGroupName] = useState<string>("");
    const [ragFlag, setRagFlag] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<string>("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAddGroupData();
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!groupCd) {
            setError("グループCDは必須です");
            setSubmitting(false);
            return;
        }
        if (!/^[0-9]+$/.test(groupCd)) {
            setError("グループCDは2桁以内の半角数字で入力してください");
            setSubmitting(false);
            return;
        }
        if (!groupName) {
            setError("グループ名は必須です");
            setSubmitting(false);
            return;
        }
        if (groupName.includes("'")) {
            setError("グループ名にシングルクォーテーションは使用できません");
            setSubmitting(false);
            return;
        }

        try {
            const result = await addGroup({
                group_cd: groupCd,
                group_name: groupName,
                check_setting_rag_flag: ragFlag,
                select_model: selectedModel
            });

            if (result.success) {
                navigate("/menu/groups");
            } else {
                setError(result.message || "登録に失敗しました");
            }
        } catch (err) {
            setError("登録中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getModelOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return [
            { key: '', text: '選択してください' },
            ...data.models.map((m) => ({
                key: m.id,
                text: m.name
            }))
        ];
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="グループ登録" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <TextField
                    name="group_cd"
                    label="グループCD"
                    value={groupCd}
                    onChange={(_, newValue) => setGroupCd(newValue || "")}
                    required
                    maxLength={2}
                    description="※2桁以内の半角数字で入力してください。"
                    autoComplete="off"
                />

                <TextField
                    name="group_name"
                    label="グループ名"
                    value={groupName}
                    onChange={(_, newValue) => setGroupName(newValue || "")}
                    required
                    maxLength={100}
                    description="※シングルクォーテーションは使用できません。"
                    autoComplete="off"
                />

                <Checkbox
                    label="ログイン時に文書検索モードを有効にする"
                    checked={ragFlag}
                    onChange={(_, checked) => setRagFlag(checked || false)}
                />

                <Dropdown
                    label="デフォルトモデル選択"
                    options={getModelOptions()}
                    selectedKey={selectedModel}
                    onChange={(_, option) => setSelectedModel((option?.key as string) || "")}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !groupCd || !groupName}
                    />
                    <LinkButton link_to="/menu/groups" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}